import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileSpreadsheet, RefreshCw, Search, Hash, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { downloadExcel } from '../../utils/excel';
import ProjectSelect from '../../components/ProjectSelect';

// Reports & Analytics → Auto Proto Codes: every code the Settings series gave,
// with project, item, order, date and who created it.
const fmtDate = d => { if (!d) return '—'; const x = new Date(d); return isNaN(x) ? '—' : x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); };
const fmtDateTime = d => { if (!d) return ''; const x = new Date(d); return isNaN(x) ? '' : x.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
const projectText = r => (r.project_name ? `${r.project_name}${r.project_code ? ` (${r.project_code})` : ''}` : '');
const MO_STATUS = { draft: 'Draft', submitted: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' };

export default function AutoProtoCodeReport() {
  const navigate = useNavigate();
  const area = useLocation().pathname.startsWith('/worker') ? '/worker' : '/admin';
  const [f, setF] = useState({ from: '', to: '', project_id: '', source: '', in_project: '', q: '' });
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const set = patch => setF(x => ({ ...x, ...patch }));

  useEffect(() => {
    api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : (r.data.rows || r.data.projects || []))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const r = await api.get('/reports/auto-proto-codes', { params });
      setData(r.data);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { const t = setTimeout(load, f.q ? 300 : 0); return () => clearTimeout(t); }, [load, f.q]);

  const rows = data?.rows || [];
  const s = data?.summary || {};

  const exportExcel = async () => {
    if (!rows.length) return toast.error('Nothing to export');
    setExporting(true);
    try {
      const filters = [
        ['Auto Proto Code Report'], [],
        ['From', f.from || 'All'], ['To', f.to || 'All'],
        ['Project', projectText(projects.find(p => String(p.id) === String(f.project_id)) || {}) || 'All'],
        ['Source', f.source === 'mo' ? 'Manufacturing Order' : f.source === 'project' ? 'Added on Project' : 'All'],
        ['In Project', f.in_project === 'yes' ? 'Yes' : f.in_project === 'no' ? 'No' : 'All'],
        ['Search', f.q || '—'], [],
        ['Total codes', s.total], ['From Manufacturing Orders', s.from_mo], ['Added on Project', s.from_project],
        ['In Project', s.in_project], ['Not in Project yet', s.not_in_project],
        ['Projects', s.projects], ['Manufacturing Orders', s.orders],
        ['First code', s.first_code || ''], ['Last code', s.last_code || ''],
      ];
      await downloadExcel(`Auto_Proto_Codes_${new Date().toISOString().slice(0, 10)}.xlsx`, [
        { name: 'Summary', rows: filters },
        { name: 'Proto Codes', rows: rows.map((r, i) => ({
          'S.No': i + 1, 'Proto Code': r.proto_code, 'Item': r.item_name || '', 'Area': r.area || '', 'Qty': r.qty,
          'Project Name': r.project_name || '', 'Project Code': r.project_code || '',
          'Manufacturing Order': r.po_no || '', 'Order Status': MO_STATUS[r.mo_status] || '',
          'Source': r.source_label, 'In Project': r.in_project ? 'Yes' : 'No',
          'Date Given': fmtDateTime(r.given_at), 'Created By': r.created_by_name || '',
        })) },
        { name: 'By Project', rows: (data.by_project || []).map(g => ({
          'Project Name': g.project_name || (g.po_no ? `(no project yet) ${g.po_no}` : ''), 'Project Code': g.project_code || '',
          'Codes': g.codes, 'First Code': g.first, 'Last Code': g.last,
        })) },
      ]);
    } catch (err) { toast.error(err.message || 'Export failed'); }
    finally { setExporting(false); }
  };

  const Card = ({ label, value, cls }) => (
    <div className={`rounded-xl border p-3 ${cls}`}>
      <div className="text-2xl font-bold">{value ?? 0}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-bold text-gray-900 flex items-center gap-2"><Hash className="w-5 h-5 text-purple-600" /> Auto Proto Codes</h2>
          <p className="text-xs text-gray-500">Codes given automatically from the Settings series{s.first_code ? ` · ${s.first_code} → ${s.last_code}` : ''}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} /></button>
          <button onClick={exportExcel} disabled={exporting} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-50">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Export Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card label="Codes Generated" value={s.total} cls="bg-purple-50 border-purple-200 text-purple-800" />
        <Card label="From Manufacturing Orders" value={s.from_mo} cls="bg-blue-50 border-blue-200 text-blue-800" />
        <Card label="Added on Project" value={s.from_project} cls="bg-indigo-50 border-indigo-200 text-indigo-800" />
        <Card label="In Project" value={s.in_project} cls="bg-green-50 border-green-200 text-green-800" />
        <Card label="Not in Project yet" value={s.not_in_project} cls="bg-amber-50 border-amber-200 text-amber-800" />
        <Card label={`Projects · ${s.orders || 0} orders`} value={s.projects} cls="bg-gray-50 border-gray-200 text-gray-800" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-3 grid grid-cols-2 md:grid-cols-6 gap-2">
        <label className="text-xs text-gray-500">From<input type="date" value={f.from} onChange={e => set({ from: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" /></label>
        <label className="text-xs text-gray-500">To<input type="date" value={f.to} onChange={e => set({ to: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" /></label>
        <div className="text-xs text-gray-500 col-span-2 md:col-span-1">Project
          <ProjectSelect projects={projects} value={f.project_id} onChange={v => set({ project_id: v })} placeholder="All projects" format="name-code"
            className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-800" />
        </div>
        <label className="text-xs text-gray-500">Source
          <select value={f.source} onChange={e => set({ source: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-800">
            <option value="">All</option><option value="mo">Manufacturing Order</option><option value="project">Added on Project</option>
          </select>
        </label>
        <label className="text-xs text-gray-500">In Project
          <select value={f.in_project} onChange={e => set({ in_project: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white text-gray-800">
            <option value="">All</option><option value="yes">Yes</option><option value="no">Not yet</option>
          </select>
        </label>
        <label className="text-xs text-gray-500 col-span-2 md:col-span-1">Search
          <span className="mt-1 flex items-center gap-1.5 border border-gray-200 rounded-lg px-2 py-1.5">
            <Search className="w-3.5 h-3.5 text-gray-400" />
            <input value={f.q} onChange={e => set({ q: e.target.value })} placeholder="Code, item, order…" className="flex-1 min-w-0 text-sm outline-none text-gray-800" />
          </span>
        </label>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading && !data ? <div className="p-10 text-center text-sm text-gray-400">Loading…</div>
          : !rows.length ? <div className="p-10 text-center text-sm text-gray-400">No automatic Proto Codes for these filters</div>
          : (
            <table className="w-full text-sm min-w-[980px]">
              <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-3 py-2.5">#</th><th className="px-3 py-2.5">Proto Code</th><th className="px-3 py-2.5">Item</th>
                <th className="px-3 py-2.5">Project</th><th className="px-3 py-2.5">Manufacturing Order</th><th className="px-3 py-2.5">Source</th>
                <th className="px-3 py-2.5">In Project</th><th className="px-3 py-2.5">Date</th><th className="px-3 py-2.5">Created By</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r, i) => (
                  <tr key={r.key} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-2 font-mono font-semibold text-purple-700">{r.proto_code}</td>
                    <td className="px-3 py-2"><div className="font-medium text-gray-800">{r.item_name}</div>{(r.area || r.qty) ? <div className="text-xs text-gray-400">{[r.area, r.qty ? `Qty ${r.qty}` : ''].filter(Boolean).join(' · ')}</div> : null}</td>
                    <td className="px-3 py-2">{r.project_id
                      ? <button onClick={() => navigate(`${area}/projects/${r.project_id}`)} className="text-left hover:underline"><div className="text-gray-800">{r.project_name}</div><div className="text-xs text-gray-400">{r.project_code}</div></button>
                      : <span className="text-xs text-gray-400">No project yet</span>}</td>
                    <td className="px-3 py-2">{r.main_po_id
                      ? <button onClick={() => navigate(`${area}/main-po/${r.main_po_id}`)} className="text-left hover:underline"><div className="text-blue-700 font-medium">{r.po_no}</div><div className="text-xs text-gray-400">{MO_STATUS[r.mo_status] || ''}</div></button>
                      : <span className="text-gray-300">—</span>}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.source_label}</td>
                    <td className="px-3 py-2">{r.in_project
                      ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">Yes</span>
                      : <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">Not yet</span>}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtDate(r.given_at)}</td>
                    <td className="px-3 py-2 text-gray-600">{r.created_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {(data?.by_project || []).length > 1 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <div className="px-4 py-2.5 border-b border-gray-100 font-semibold text-gray-700 text-sm">By Project</div>
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500"><th className="px-3 py-2">Project</th><th className="px-3 py-2 text-right">Codes</th><th className="px-3 py-2">First</th><th className="px-3 py-2">Last</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {data.by_project.map((g, i) => (
                <tr key={i}><td className="px-3 py-2">{g.project_name ? `${g.project_name}${g.project_code ? ` (${g.project_code})` : ''}` : <span className="text-gray-400">No project yet · {g.po_no}</span>}</td>
                  <td className="px-3 py-2 text-right font-bold">{g.codes}</td><td className="px-3 py-2 font-mono text-xs">{g.first}</td><td className="px-3 py-2 font-mono text-xs">{g.last}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
